import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  CryptotokenDataActions,
  CryptotokenDataPipes,
  CryptotokenDataSelectors,
  CryptotokenDeleteActions,
  CryptotokenDeleteSelectors,
} from './cryptotoken-data.feature';
import { Observable, of, tap } from 'rxjs';
import { PageDataState } from '../../page-data/page-data.types';
import { CryptoToken } from 'src/app/shared/entities/crypto-token';
import { ApiService } from 'src/app/shared/services/api/api.service';

@Injectable({
  providedIn: 'root',
})
export class CryptotokenDataService {
  private store = inject(Store);
  private apiService = inject(ApiService);

  data$: Observable<CryptoToken[] | null> = this.store.pipe(
    CryptotokenDataPipes.selectData,
  );

  isLoading$: Observable<boolean> = this.store.select(
    CryptotokenDataSelectors.selectIsLoading,
  );

  isLoaded$: Observable<boolean> = this.store.select(
    CryptotokenDataSelectors.selectIsLoaded,
  );
  isError$: Observable<boolean> = this.store.select(
    CryptotokenDataSelectors.selectIsError,
  );
  state$: Observable<PageDataState<void, CryptoToken[]>> = this.store.select(
    CryptotokenDataSelectors.selectPageDataState,
  );

  isDeleting: Observable<boolean> = this.store.select(
    CryptotokenDeleteSelectors.isDeleting,
  );

  ensure(options?: { suppressErrorNotification?: boolean }) {
    this.store.dispatch(
      CryptotokenDataActions.ensure({
        dataParams: undefined,
        suppressErrorNotification: options?.suppressErrorNotification,
      }),
    );
  }

  clear() {
    this.store.dispatch(CryptotokenDataActions.clear());
  }

  transform(transformation: (data: CryptoToken[]) => CryptoToken[]) {
    this.store.dispatch(CryptotokenDataActions.transform({ transformation }));
  }

  delete(token: CryptoToken) {
    this.store.dispatch(CryptotokenDeleteActions.delete({ item: token }));
  }

  activate(token: CryptoToken, password: string): Observable<void> {
    const tokenName = token.tokenName;
    if (!tokenName) {
      return of();
    }
    return this.apiService
      .activateToken(tokenName, password)
      .pipe(
        tap(() =>
          this.transform(data =>
            data.map(ct =>
              ct.tokenName === tokenName ? { ...ct, active: true } : ct,
            ),
          ),
        ),
      );
  }

  deactivate(token: CryptoToken): Observable<void> {
    const tokenName = token.tokenName;
    if (!tokenName) {
      return of();
    }
    return this.apiService
      .deactivateToken(tokenName)
      .pipe(
        tap(() =>
          this.transform(data =>
            data.map(ct =>
              ct.tokenName === tokenName ? { ...ct, active: false } : ct,
            ),
          ),
        ),
      );
  }
}
